/**
 * Book → Tech Support (1.5.0): one question per screen — who → a time on a tech day → where → what they need help
 * with → the device → the confirm card. A single choice opens the next question by itself; Back keeps what was
 * typed. The tenant gets "Tech booked" and the morning-of reminder (their own link); the associate gets the
 * ticket — each through the Outbox. Counts for ABF only, never VISIONS.
 *
 * 1.6.7 (D-1.6.7-4): a person who is not onboarded for Tech Support (the agreement not completed) is not offered a
 * day: in place of the day and time chips the sheet says why and offers Complete it for them / Send by email
 * (OnboardingGate). Once the agreement is recorded the chips are there, without reopening the sheet. An existing
 * Tech Support tenant is not re-gated — for them 1.5.3's check at the confirm card stands.
 */
import { useEffect, useRef, useState, type RefObject } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TenantSummary } from '@shared/api-types';
import { formatClock } from '@shared/dayplan';
import { HELP_KEYS, type HelpKey, type TechSlotsResponse } from '@shared/tech';
import type { TechPlace } from '@shared/settings-catalog';
import { apiFetch, ApiRequestError } from '@/lib/api';
import { formatDayMedium, formatDayShort } from '@/lib/dates';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { TenantSearch } from '@/components/TenantSearch';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { SheetContent } from '@/components/ui/sheet';
import { Link } from 'react-router-dom';
import type { AgreementState } from '@shared/agreements';
import { OnboardingGate } from '@/features/agreements/OnboardingGate';
import type { BookProgram } from '@shared/book-programs';
import { ProgramPicker } from '@/features/booking/ProgramPicker';
import { onboardingKey, useOnboarding } from '@/features/agreements/onboarding';

const STEPS = ['who', 'when', 'where', 'what', 'device', 'confirm'] as const;
type Step = (typeof STEPS)[number];

export function TechBookSheet({ tenant: fixed, onDone, onProgram, returnFocusTo, initial }: { initial?: { help: HelpKey[]; other: string; device: string; description: string }; tenant?: { tenantId: string; unit: string; name: string } | null; onDone: () => void; /** 1.8.3: Book's "what" row — another program was pressed (the tenant chosen so far goes with it). */ onProgram?: (program: BookProgram, tenant: { tenantId: string; unit: string; name: string } | null) => void; returnFocusTo?: RefObject<HTMLElement | null> }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const announce = useAnnounce();
  const toast = useToast();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>(fixed ? 'when' : 'who');
  const [tenant, setTenant] = useState(fixed ?? null);
  const [dayKey, setDayKey] = useState<string | null>(null);
  const [minutes, setMinutes] = useState<number | null>(null);
  const [place, setPlace] = useState<TechPlace | null>(null);
  const [help, setHelp] = useState<HelpKey[]>(initial?.help ?? []);
  const [other, setOther] = useState(initial?.other ?? '');
  const [device, setDevice] = useState(initial?.device ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [emailTenant, setEmailTenant] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 1.5.3: Book refused a phone / in-person booking — no agreement on file (counsel's words, and why). */
  const [refusal, setRefusal] = useState<{ message: string; detail: string | null } | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);

  const slots = useQuery({ queryKey: ['tech-slots'], queryFn: () => apiFetch<TechSlotsResponse>('/api/tech/slots'), staleTime: 15_000 });
  const days = (slots.data?.days ?? []).filter((d) => !d.closed && d.slots.some((s) => s.state === 'free'));
  const day = slots.data?.days.find((d) => d.dayKey === dayKey) ?? null;
  const n = STEPS.indexOf(step) + 1;
  // 1.5.3: is the Tech Support agreement on file? (a phone or in-person booking needs it)
  const agreement = useQuery({
    queryKey: ['tech-agreement', tenant?.tenantId],
    queryFn: () => apiFetch<{ ready: boolean; state: AgreementState }>(`/api/tech/agreement?tenantId=${encodeURIComponent(tenant!.tenantId)}`),
    enabled: !!tenant && step === 'confirm',
    staleTime: 10_000,
  });

  // 1.6.7: is this person onboarded for Tech Support? (the server refuses the booking either way)
  const onboarding = useOnboarding(tenant?.tenantId);
  const techOnb = onboarding.data?.onboarding?.tech ?? null;
  const gated = !!tenant && !!techOnb && !techOnb.complete;
  const recorded = async () => {
    if (!tenant) return;
    await qc.invalidateQueries({ queryKey: onboardingKey(tenant.tenantId) });
    void qc.invalidateQueries({ queryKey: ['tech-agreement', tenant.tenantId] });
    void qc.invalidateQueries({ queryKey: ['person', tenant.tenantId] });
    heading.current?.focus();
  };

  useEffect(() => {
    heading.current?.focus();
    announce(t('techBook.stepAnnounce', { n, of: STEPS.length, q: t(`techBook.q.${step}`) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const go = (s: Step) => {
    setError(null);
    setRefusal(null);
    setStep(s);
  };
  const back = () => go(STEPS[Math.max(0, STEPS.indexOf(step) - (fixed && step === 'when' ? 0 : 1))]!);
  const helpWords = (k: HelpKey) => (k === 'other' && other ? other : t(`techBook.help.${k}`));

  const submit = async () => {
    if (!tenant || !dayKey || minutes === null || !place) return;
    setBusy(true);
    setError(null);
    setRefusal(null);
    try {
      await apiFetch('/api/tech/book', { method: 'POST', body: JSON.stringify({ tenantId: tenant.tenantId, dayKey, minutes, place, help, other: other.trim(), device: device.trim(), description: description.trim(), emailTenant }) });
      const m = t('techBook.booked', { unit: tenant.unit, day: formatDayShort(dayKey, lang), time: formatClock(minutes) });
      toast(m);
      announce(m);
      void qc.invalidateQueries();
      onDone();
    } catch (e) {
      const m = e instanceof ApiRequestError ? (e.body?.message ?? e.message) : (e as Error).message;
      if (e instanceof ApiRequestError && e.body?.error === 'no-agreement') {
        // 1.6.7: the server's word is the truth — read the onboarding again (the gate shows on the When step)
        void qc.invalidateQueries({ queryKey: onboardingKey(tenant.tenantId) });
        setRefusal({ message: m, detail: e.body.detail ?? null });
        announce(m, 'assertive');
        return;
      }
      setError(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };

  let body: React.ReactNode = null;
  if (slots.data && !slots.data.ready) {
    body = (
      <div className="warn take" role="alert" data-testid="tech-not-ready">
        <Icon name="alert" />
        <div>
          {slots.data.notReady.map((x) => (
            <div key={x}>{x}</div>
          ))}
        </div>
      </div>
    );
  } else if (step === 'who') {
    body = (
      <TenantSearch
        label={t('book.who')}
        placeholder={t('book.whoPlaceholder')}
        testId="tech-tenant-search"
        onOpen={(s: TenantSummary) => {
          setTenant({ tenantId: s.tenantId, unit: s.unit, name: s.name });
          go('when');
        }}
      />
    );
  } else if (step === 'when') {
    body = !slots.data ? (
      <p className="help">{t('app.loading')}</p>
    ) : tenant && onboarding.isLoading ? (
      <p className="help" role="status" data-testid="tech-onboarding-checking">
        {t('onb167.checking')}
      </p>
    ) : gated && tenant && techOnb ? (
      // 1.6.7: not onboarded for Tech Support — the reason and the two presses, in place of the chips
      <OnboardingGate tenant={tenant} onboarding={techOnb} onRecorded={() => void recorded()} onLeave={onDone} />
    ) : days.length === 0 ? (
      <p className="help" role="status">
        {t('techBook.noDays')}
      </p>
    ) : (
      <>
        <div className="chips" role="group" aria-label={t('techBook.day')} data-testid="tech-days">
          {days.map((d) => (
            <button key={d.dayKey} type="button" className="chip slot" aria-pressed={d.dayKey === dayKey} onClick={() => setDayKey(d.dayKey)} data-testid={`tech-day-${d.dayKey}`}>
              <span className="t">{formatDayShort(d.dayKey, lang)}</span>
              <span className="sub tnum">{t('techBook.free', { count: d.slots.filter((s) => s.state === 'free').length })}</span>
            </button>
          ))}
        </div>
        {(slots.data.days.filter((d) => d.closed) ?? []).length ? (
          <p className="help">{t('techBook.closedDays', { list: slots.data.days.filter((d) => d.closed).map((d) => `${formatDayShort(d.dayKey, lang)} (${d.closed})`).join(' · ') })}</p>
        ) : null}
        {day ? (
          <div className="chips" role="group" aria-label={t('techBook.time', { day: formatDayMedium(day.dayKey, lang) })} data-testid="tech-times" style={{ marginTop: 12 }}>
            {day.slots.map((s) =>
              s.state !== 'free' ? (
                // marked, never offered (1.5.1 rule 4)
                <span key={s.minutes} className="chip slot locked marker" data-testid={`tech-time-${s.minutes}`} data-state={s.state} data-offered="false">
                  <span className="t tnum" aria-hidden="true">
                    {formatClock(s.minutes)}
                  </span>
                  <span className="sub" aria-hidden="true">
                    {t(`techBook.state.${s.state}`)}
                  </span>
                  <span className="sr-only">{`${formatClock(s.minutes)}–${formatClock(s.endMinutes)}, ${t(`techBook.state.${s.state}`)}`}</span>
                </span>
              ) : (
              <button
                key={s.minutes}
                type="button"
                className="chip slot"
                aria-pressed={minutes === s.minutes}
                aria-label={`${formatClock(s.minutes)}–${formatClock(s.endMinutes)}, ${t(`techBook.state.${s.state}`)}`}
                onClick={() => {
                  setMinutes(s.minutes);
                  go('where');
                }}
                data-testid={`tech-time-${s.minutes}`}
              >
                <span className="t tnum">{formatClock(s.minutes)}</span>
                <span className="sub">{t(`techBook.state.${s.state}`)}</span>
              </button>
              ),
            )}
          </div>
        ) : null}
      </>
    );
  } else if (step === 'where') {
    body = (
      <div className="big-choices" role="group" aria-labelledby="tech-q">
        {(slots.data?.places ?? []).map((p) => (
          <button
            key={p}
            type="button"
            className="big-choice"
            aria-pressed={place === p}
            onClick={() => {
              setPlace(p);
              go('what');
            }}
            data-testid={`tech-place-${p}`}
          >
            {p === 'Apartment' && tenant ? t('techBook.apartment', { unit: tenant.unit }) : t(`tech.place.${p}`)}
          </button>
        ))}
      </div>
    );
  } else if (step === 'what') {
    body = (
      <>
        <fieldset className="checks big" aria-labelledby="tech-q" data-testid="tech-help">
          {HELP_KEYS.map((k) => (
            <label key={k} className="check">
              <input type="checkbox" checked={help.includes(k)} onChange={(e) => setHelp(e.target.checked ? [...help, k] : help.filter((x) => x !== k))} data-testid={`tech-help-${k}`} />
              <span>{t(`techBook.help.${k}`)}</span>
            </label>
          ))}
        </fieldset>
        {help.includes('other') ? (
          <div className="field">
            <label htmlFor="tech-other">{t('techBook.otherLabel')}</label>
            <input id="tech-other" className="input" value={other} maxLength={200} onChange={(e) => setOther(e.target.value)} data-testid="tech-other" />
          </div>
        ) : null}
        <div className="field">
          <label htmlFor="tech-desc">{t('techBook.description')}</label>
          {/* 1.8.5 (D-1.8.5-0b): the Tech Wizard reads this box — and only this box — so it asks for no names or numbers */}
          <p className="help" id="tech-desc-privacy" data-testid="tech-description-privacy">
            {t('techBook.privacy')}
          </p>
          <textarea id="tech-desc" className="input" value={description} maxLength={2000} onChange={(e) => setDescription(e.target.value)} placeholder={t('techBook.descriptionPlaceholder')} aria-describedby="tech-desc-privacy" data-testid="tech-description" />
        </div>
      </>
    );
  } else if (step === 'device') {
    body = (
      <div className="field">
        <label htmlFor="tech-device">{t('techBook.deviceLabel')}</label>
        <input id="tech-device" className="input" value={device} maxLength={200} onChange={(e) => setDevice(e.target.value)} placeholder={t('techBook.devicePlaceholder')} data-testid="tech-device" />
        <span className="help">{t('techBook.deviceHelp')}</span>
      </div>
    );
  } else if (step === 'confirm' && tenant && dayKey && minutes !== null && place) {
    body = (
      <div className="card confirm-card" data-testid="tech-confirm">
        <div className="card-b" style={{ display: 'grid', gap: 6 }}>
          <div>
            <b>{t('techBook.c.who')}</b> {tenant.unit} · {tenant.name}
          </div>
          <div>
            <b>{t('techBook.c.when')}</b> {formatDayMedium(dayKey, lang)}, {formatClock(minutes)}–{formatClock(minutes + (slots.data?.lengthMinutes ?? 45))}
          </div>
          <div>
            <b>{t('techBook.c.where')}</b> {place === 'Apartment' ? t('techBook.apartment', { unit: tenant.unit }) : t(`tech.place.${place}`)}
          </div>
          <div>
            <b>{t('techBook.c.what')}</b> {help.map(helpWords).join(', ')}
          </div>
          <div>
            <b>{t('techBook.c.device')}</b> {device}
          </div>
          {description ? (
            <div>
              <b>{t('techBook.c.said')}</b> {description}
            </div>
          ) : null}
          <label className="switch" style={{ marginTop: 8 }}>
            <input type="checkbox" checked={emailTenant} onChange={(e) => setEmailTenant(e.target.checked)} data-testid="tech-email" />
            <span>{t('techBook.emailTenant')}</span>
          </label>
          <p className="help">{t('techBook.emailsNote')}</p>
          {agreement.data?.ready ? (
            <p className="help" data-testid="tech-agreement-status">
              <b>{t('agreement.book.label')}</b>{' '}
              {agreement.data.state.onFile
                ? t(`agreement.book.onFile${agreement.data.state.status}`, { date: agreement.data.state.date ? formatDayShort(agreement.data.state.date.slice(0, 10).replace(/-/g, ''), lang) : '' })
                : emailTenant
                  ? t('agreement.book.noneEmail')
                  : t('agreement.book.nonePhone')}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  const nextOk = step === 'what' ? help.length > 0 && (!help.includes('other') || !!other.trim()) : step === 'device' ? !!device.trim() : false;

  return (
    <SheetContent
      title={tenant ? `${t('techBook.title')} · ${tenant.unit}` : t('techBook.title')}
      returnFocusTo={returnFocusTo}
      data-testid="tech-book-sheet"
      footer={
        <>
          {step !== 'who' && !(fixed && step === 'when') ? (
            <Button variant="secondary" type="button" onClick={back} data-testid="tech-back">
              {t('techBook.back')}
            </Button>
          ) : (
            <Button variant="secondary" type="button" onClick={onDone}>
              {t('book.cancel')}
            </Button>
          )}
          {step === 'what' || step === 'device' ? (
            <Button type="button" disabled={!nextOk} onClick={() => go(step === 'what' ? 'device' : 'confirm')} data-testid="tech-next">
              {t('techBook.next')}
            </Button>
          ) : step === 'confirm' ? (
            <Button type="button" disabled={busy} onClick={() => submit()} data-testid="tech-submit">
              {busy ? t('book.sending') : t('techBook.book')}
            </Button>
          ) : null}
        </>
      }
    >
      {onProgram ? <ProgramPicker value="tech" onChange={(p) => onProgram(p, tenant)} /> : null}
      <p className="eyebrow">{t('techBook.stepOf', { n, of: STEPS.length })}</p>
      <h3 id="tech-q" ref={heading} tabIndex={-1} className="step-q" data-testid="tech-question">
        {t(`techBook.q.${step}`)}
      </h3>
      {body}
      {refusal && tenant ? (
        <div className="warn take" role="alert" data-testid="tech-no-agreement">
          <Icon name="alert" />
          <div style={{ display: 'grid', gap: 8 }}>
            <b data-testid="tech-no-agreement-message">{refusal.message}</b>
            {refusal.detail ? <span>{refusal.detail}</span> : null}
            <div className="row-actions" style={{ flexWrap: 'wrap' }}>
              <a className="btn sm" href="/api/tech/agreement/print" target="_blank" rel="noopener" data-testid="tech-print-agreement">
                {t('agreement.book.print')}
              </a>
              <Link className="btn secondary sm" to={`/people/${encodeURIComponent(tenant.tenantId)}`} onClick={onDone} data-testid="tech-open-record">
                {t('agreement.book.record', { unit: tenant.unit })}
              </Link>
            </div>
          </div>
        </div>
      ) : null}
      {error ? (
        <div className="warn take" role="alert" data-testid="tech-error">
          <Icon name="alert" />
          <div>{error}</div>
        </div>
      ) : null}
    </SheetContent>
  );
}
